import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FieldProps, ScreenProperty } from '../../../../../../../@types/types'
import { BasicInfoScreenValidationResult } from '../../../../../../../@types/validation-types'
import ToggleSwitch from '../../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import StartRentalDate from '../startRentalDate/StartRentalDate'
import { getNormalizedToday, isSameDay } from '../../../../../../../utils/date-utils'

export interface StartRentalDateWithToggleProps extends FieldProps<BasicInfoScreenValidationResult> {
  doReset: (clearingUpOf: ScreenProperty[]) => void
}

const elementId = 'start-rental-date-with-toggle'

const StartRentalDateWithToggle: React.FC<StartRentalDateWithToggleProps> = ({
  insertionData,
  validationMessages,
  screenName,
  wrapperClassName = 'grid-item one-whole',
  onChange,
  doReset,
}) => {
  const { t } = useTranslation()

  const today = useMemo(() => getNormalizedToday(), [])

  const startRentalDate = insertionData.startRentalDate
  const isCurrentDateSameAsToday = startRentalDate ? isSameDay(new Date(startRentalDate), today) : false

  const isAutoCalculated = !startRentalDate || isCurrentDateSameAsToday
  const [isImmediatelyAvailable, setIsImmediatelyAvailable] = useState(isAutoCalculated)

  // Handle date synchronization and toggle state
  useEffect(() => {
    if (isImmediatelyAvailable && !startRentalDate) {
      // Set today's date when toggle is on and no date is set
      const screenProperty: ScreenProperty = {
        screen: screenName,
        property: 'startRentalDate',
      }
      const todayDate = getNormalizedToday()
      onChange(screenProperty, todayDate)
    } else if (startRentalDate && isSameDay(new Date(startRentalDate), today)) {
      // Keep toggle state in sync when date is set to today
      setIsImmediatelyAvailable(true)
    }
  }, [isImmediatelyAvailable, startRentalDate, screenName, onChange, today])

  // Handle toggle switch changes
  const doToggle = () => {
    const newValue = !isImmediatelyAvailable
    setIsImmediatelyAvailable(newValue)

    if (newValue) {
      // When switching to immediately available, set today's date
      const screenProperty: ScreenProperty = {
        screen: screenName,
        property: 'startRentalDate',
      }
      const todayDate = getNormalizedToday()
      onChange(screenProperty, todayDate)
    } else {
      // When switching to custom date, clear the field
      callReset()
    }
  }

  const callReset = () => {
    const resetForStartRentalDate: ScreenProperty = {
      screen: screenName,
      property: 'startRentalDate',
    }
    doReset([resetForStartRentalDate])
  }

  return (
    <div className={wrapperClassName}>
      <div className='grid'>
        <div className='grid-item'>
          <ToggleSwitch
            id={elementId}
            alignment='maxSpread'
            propertyName='custom'
            label={
              <span>
                {t('wizard.screen.basicInfo.room.immediatelyAvailable', 'Diese Immobilie ist absofort bezugsfrei')}
              </span>
            }
            value={isImmediatelyAvailable}
            onChange={doToggle}
          />
        </div>
        {!isImmediatelyAvailable && (
          <div className='grid-item one-whole margin-top-m'>
            <StartRentalDate
              insertionData={insertionData}
              validationMessages={validationMessages}
              screenName={screenName}
              wrapperClassName=''
              onChange={onChange}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default StartRentalDateWithToggle
